import React, { useState, useMemo, useRef } from 'react';
import { User as UserIcon, CreditCard, Calendar, LogOut, AlertTriangle, ShieldCheck, Crown, ScrollText, Star, Award, Camera, Trash2, Edit2, X, Check, Loader2, Lock, Globe, EyeOff } from 'lucide-react';
import { User, CigarLog } from '../types';
import { claimCommunityName } from '../services/userService';
import { isAndroid } from '../utils/platformUtils';
import { getUserAvatarUrl } from '../utils/avatar';
import logo from '../assets/logo.jpg';

interface ProfileProps {
    user: User;
    logs: CigarLog[];
    onCancelSubscription: () => void;
    onLogout: () => void;
    onUpdateUser: (user: User) => void;
}

export const Profile: React.FC<ProfileProps> = ({ user, logs, onCancelSubscription, onLogout, onUpdateUser }) => {
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    const [isEditingProfile, setIsEditingProfile] = useState(false);
    const [newName, setNewName] = useState(user.displayName);
    const [newCommunityName, setNewCommunityName] = useState(user.communityName || '');
    const [communityNameError, setCommunityNameError] = useState('');
    const [isCheckingName, setIsCheckingName] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const socialEnabled = !isAndroid();

    // Calculate Stats
    const stats = useMemo(() => {
        const totalLogged = logs.length;
        const avgRating = totalLogged > 0
            ? (logs.reduce((acc, curr) => acc + curr.rating, 0) / totalLogged).toFixed(1)
            : '-';

        // Find favorite brand
        const brandCounts: { [key: string]: number } = {};
        logs.forEach(log => {
            const brand = log.brand.trim();
            brandCounts[brand] = (brandCounts[brand] || 0) + 1;
        });
        const favoriteBrand = Object.entries(brandCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || '-';

        return { totalLogged, avgRating, favoriteBrand };
    }, [logs]);

    const handleCoverPhotoClick = () => {
        fileInputRef.current?.click();
    };

    const handleRemoveCoverPhoto = (e: React.MouseEvent) => {
        e.stopPropagation(); // Prevent triggering file input if overlayed
        if (window.confirm('Are you sure you want to remove your custom cover photo?')) {
            onUpdateUser({
                ...user,
                coverPhotoURL: undefined
            });
        }
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                if (reader.result) {
                    onUpdateUser({
                        ...user,
                        coverPhotoURL: reader.result as string
                    });
                }
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSaveProfile = async () => {
        if (!newName.trim()) return;

        try {
            let communityNameUpdate = {};

            // Only process community name if social features are enabled, changed, AND user is subscribed
            if (socialEnabled && user.isSubscribed && newCommunityName.trim() && newCommunityName !== user.communityName) {
                setIsCheckingName(true);
                setCommunityNameError('');

                // Final reservation check before persisting.
                const nameClaimed = await claimCommunityName(user.uid, newCommunityName, user.communityName);
                if (!nameClaimed) {
                    setCommunityNameError('This Community Name is already taken.');
                    setIsCheckingName(false);
                    return;
                }

                communityNameUpdate = { communityName: newCommunityName.trim() };
            }

            // 3. Update User Profile
            onUpdateUser({
                ...user,
                displayName: newName.trim(),
                ...communityNameUpdate
            });

            setIsEditingProfile(false);
        } catch (error) {
            console.error("Error updating profile:", error);
            setCommunityNameError('Failed to update profile. Please try again.');
        } finally {
            setIsCheckingName(false);
        }
    };

    return (
        <div className="h-full w-full overflow-y-auto bg-brand-950 pb-20">

            {/* Cover Photo Section */}
            <div className="relative h-64 w-full overflow-hidden group bg-brand-900">
                <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/50 z-10 pointer-events-none"></div>
                <img
                    src={user.coverPhotoURL || logo}
                    alt="Lounge Cover"
                    className="w-full h-full object-cover opacity-90 transition-opacity group-hover:opacity-80"
                />
                <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    accept="image/*"
                    className="hidden"
                />

                {/* Cover Photo Actions */}
                <div className="absolute top-4 right-4 z-20 flex gap-3 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-300">
                    {user.coverPhotoURL && (
                        <button
                            onClick={handleRemoveCoverPhoto}
                            className="bg-black/40 hover:bg-red-600 backdrop-blur-md text-white p-2.5 rounded-full transition-all border border-white/20 shadow-xl"
                            title="Remove Cover Photo"
                        >
                            <Trash2 size={20} />
                        </button>
                    )}
                    <button
                        onClick={handleCoverPhotoClick}
                        className="bg-black/40 hover:bg-gold-500 backdrop-blur-md text-white hover:text-brand-950 p-2.5 rounded-full transition-all border border-white/20 shadow-xl"
                        title="Change Cover Photo"
                    >
                        <Camera size={20} />
                    </button>
                </div>
            </div>

            <div className="max-w-5xl mx-auto px-6 -mt-20 relative z-20">

                {/* Profile Header Card */}
                <div className="bg-brand-900 border border-brand-800 rounded-3xl p-6 md:p-8 shadow-xl mb-8 flex flex-col md:flex-row items-center md:items-end gap-6">
                    <div className="relative -mt-20 md:-mt-0">
                        <div className="w-32 h-32 rounded-full border-4 border-white overflow-hidden shadow-2xl bg-brand-100">
                            <img src={getUserAvatarUrl(user.photoURL)} alt={user.displayName} className="w-full h-full object-cover" />
                        </div>
                        <div className="absolute bottom-1 right-1 bg-gold-500 text-white p-1.5 rounded-full border-4 border-white shadow-lg" title="Verified Member">
                            <ShieldCheck size={18} />
                        </div>
                    </div>

                    <div className="text-center md:text-left flex-1">
                        <div className="flex items-center justify-center md:justify-start gap-3 mb-1">
                            <div className="flex flex-col">
                                <h1 className="text-3xl font-serif font-bold text-brand-50 leading-none flex items-center gap-2">
                                    {user.displayName}
                                    {socialEnabled && user.isPublicProfile && <Globe size={18} className="text-brand-500" />}
                                    {stats.totalLogged >= 10 && (
                                        <div className="flex items-center gap-1 bg-gold-500/10 border border-gold-500/20 px-2.5 py-1 rounded-full text-[10px] font-bold text-gold-500 uppercase tracking-wider shadow-sm" title="Certified Smoker: 10+ entries">
                                            <ShieldCheck size={12} className="fill-gold-500/20" />
                                            <span>Certified Smoker</span>
                                        </div>
                                    )}
                                </h1>
                                {socialEnabled && user.communityName && <span className="text-brand-400 font-mono text-sm tracking-wide">@{user.communityName}</span>}
                            </div>
                            <button
                                onClick={() => {
                                    setNewName(user.displayName);
                                    setNewCommunityName(user.communityName || '');
                                    setCommunityNameError('');
                                    setIsEditingProfile(true);
                                }}
                                className="p-1.5 text-brand-400 hover:text-gold-500 hover:bg-brand-800 rounded-lg transition-colors"
                            >
                                <Edit2 size={16} />
                            </button>
                        </div>
                        <p className="text-brand-300 flex items-center justify-center md:justify-start gap-2 mb-4">
                            <span className="text-gold-600 font-bold text-sm uppercase tracking-wider flex items-center gap-1">
                                <Crown size={14} /> Club Member
                            </span>
                            <span className="w-1 h-1 bg-brand-300 rounded-full"></span>
                            <span className="text-sm">{user.email}</span>
                        </p>
                        <div className="inline-flex items-center gap-2 bg-brand-950 px-4 py-1.5 rounded-full text-xs font-mono text-brand-400 border border-brand-800">
                            <UserIcon size={12} className="text-brand-500" />
                            <span>ID: {user.uid.slice(0, 12)}...</span>
                        </div>
                    </div>

                    <div className="flex gap-3 items-center">
                        {/* Public Profile Toggle (disabled on Android) */}
                        {socialEnabled && (
                            <div className="flex items-center gap-3 mr-2 bg-brand-800/80 rounded-full px-4 py-2 border border-brand-700 shadow-sm transition-colors">
                                <div className={`flex items-center gap-2 ${user.isPublicProfile ? 'text-gold-400' : 'text-brand-50'}`}>
                                    {user.isPublicProfile ? <Globe size={16} /> : <EyeOff size={16} />}
                                    <span className="text-sm font-bold tracking-wide">Public Profile</span>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer ml-1">
                                    <input
                                        type="checkbox"
                                        className="sr-only peer"
                                        checked={user.isPublicProfile || false}
                                        disabled={!user.isSubscribed}
                                        onChange={(e) => {
                                            if (user.isSubscribed) {
                                                onUpdateUser({ ...user, isPublicProfile: e.target.checked });
                                            }
                                        }}
                                    />
                                    <div className="w-9 h-5 bg-brand-950 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-brand-50 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-brand-500 after:border-brand-400 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-gold-900/50 peer-checked:after:bg-gold-500 peer-checked:after:border-gold-500"></div>
                                    {!user.isSubscribed && <Lock size={10} className="absolute -top-1 -right-1 text-brand-500" />}
                                </label>
                            </div>
                        )}
                        <button
                            onClick={() => window.print()}
                            className="flex items-center gap-2 text-gold-500 hover:text-gold-400 px-5 py-2.5 rounded-xl border border-gold-500/20 bg-gold-500/5 hover:bg-gold-500/10 transition-all text-sm font-bold shadow-lg shadow-gold-500/5"
                        >
                            <ScrollText size={16} />
                            Export Journal
                        </button>
                        <button
                            onClick={onLogout}
                            className="flex items-center gap-2 text-brand-300 hover:text-brand-900 px-5 py-2.5 rounded-xl border border-brand-200 hover:bg-brand-800 transition-all text-sm font-medium"
                        >
                            <LogOut size={16} />
                            Sign Out
                        </button>
                    </div>
                </div>

                {/* Stats Dashboard */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                    <div className="bg-brand-900 border border-brand-800 p-6 rounded-2xl shadow-md relative overflow-hidden group hover:shadow-lg transition-shadow">
                        <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity transform group-hover:scale-110 text-brand-900">
                            <ScrollText size={64} />
                        </div>
                        <div className="relative z-10">
                            <div className="flex items-center gap-2 text-brand-500 mb-2 text-sm font-bold uppercase tracking-wider">
                                <ScrollText size={16} className="text-gold-600" />
                                Logs
                            </div>
                            <div className="text-4xl font-serif font-bold text-brand-50">{stats.totalLogged}</div>
                            <div className="text-xs text-brand-500 mt-1">Total cigars recorded</div>
                        </div>
                    </div>

                    <div className="bg-brand-900 border border-brand-800 p-6 rounded-2xl shadow-md relative overflow-hidden group hover:shadow-lg transition-shadow">
                        <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity transform group-hover:scale-110 text-brand-900">
                            <Star size={64} />
                        </div>
                        <div className="relative z-10">
                            <div className="flex items-center gap-2 text-brand-500 mb-2 text-sm font-bold uppercase tracking-wider">
                                <Star size={16} className="text-gold-600" />
                                Avg Rating
                            </div>
                            <div className="text-4xl font-serif font-bold text-brand-50">{stats.avgRating}</div>
                            <div className="text-xs text-brand-500 mt-1">Based on your history</div>
                        </div>
                    </div>

                    <div className="bg-brand-900 border border-brand-800 p-6 rounded-2xl shadow-md relative overflow-hidden group hover:shadow-lg transition-shadow">
                        <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity transform group-hover:scale-110 text-brand-900">
                            <Award size={64} />
                        </div>
                        <div className="relative z-10">
                            <div className="flex items-center gap-2 text-brand-500 mb-2 text-sm font-bold uppercase tracking-wider">
                                <Award size={16} className="text-gold-600" />
                                Favorite
                            </div>
                            <div className="text-2xl font-serif font-bold text-brand-50 truncate" title={stats.favoriteBrand}>{stats.favoriteBrand}</div>
                            <div className="text-xs text-brand-500 mt-1">Most logged brand</div>
                        </div>
                    </div>
                </div>

                {/* Subscription Management */}
                <div className="bg-brand-900 border border-brand-800 rounded-2xl overflow-hidden shadow-md">
                    <div className="p-6 border-b border-brand-800 flex items-center justify-between bg-brand-900">
                        <h4 className="text-lg font-serif font-bold text-brand-50 flex items-center gap-2">
                            <CreditCard className="text-gold-500" size={18} />
                            Membership & Billing
                        </h4>
                        <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border ${user.isSubscribed
                            ? 'bg-green-500/10 text-green-400 border-green-500/20'
                            : 'bg-red-500/10 text-red-400 border-red-500/20'
                            }`}>
                            {user.isSubscribed ? 'Active Member' : 'Inactive'}
                        </span>
                    </div>

                    <div className="p-6 md:p-8 space-y-8">
                        {/* Plan Details */}
                        <div className="grid md:grid-cols-2 gap-6">
                            <div>
                                <p className="text-xs text-brand-500 font-bold uppercase tracking-wider mb-2">Current Plan</p>
                                <div className="flex items-center gap-3 mb-1">
                                    <Crown className="text-gold-600" size={24} />
                                    <p className="text-xl font-serif text-brand-50">Aficionado Club Access</p>
                                </div>
                                <p className="text-brand-300 text-sm">Monthly billing of <span className="text-gold-600 font-bold">$4.99</span></p>
                            </div>

                            <div className="bg-brand-950 p-5 rounded-2xl border border-brand-800 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="p-2.5 bg-brand-900 rounded-xl text-gold-500 shadow-sm border border-brand-800">
                                        <Calendar size={20} />
                                    </div>
                                    <div>
                                        <p className="text-[10px] text-brand-500 font-bold uppercase tracking-widest">Next Renewal</p>
                                        <p className="text-brand-50 font-bold">{user.subscriptionRenewalDate || 'Automatic'}</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="border-t border-brand-800 my-6"></div>

                        {/* Billing access */}
                        <div>
                            <p className="text-xs text-brand-500 font-bold uppercase tracking-wider mb-2">Payment & Invoices</p>
                            <div className="bg-brand-950 rounded-2xl border border-brand-800 p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                                <div>
                                    <p className="text-brand-50 font-semibold">Billing is managed securely by Stripe.</p>
                                    <p className="text-brand-400 text-sm mt-1">View your payment method, invoices, and receipts in the billing portal.</p>
                                </div>
                                <a
                                    href="https://billing.stripe.com/p/login/00w5kwbwwgzwd8C9V53oA00"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-brand-900 border border-brand-700 text-gold-500 hover:text-gold-400 hover:border-gold-500/40 text-sm font-bold transition-colors whitespace-nowrap"
                                >
                                    Open Billing Portal
                                </a>
                            </div>
                        </div>

                        <div className="border-t border-brand-800 my-6"></div>

                        {/* Cancellation Zone */}
                        {showCancelConfirm ? (
                            <div className="bg-red-500/5 border border-red-500/20 rounded-2xl p-6 animate-fade-in">
                                <div className="flex items-start gap-4 mb-6">
                                    <div className="bg-red-500/10 p-3 rounded-xl shrink-0 border border-red-500/20">
                                        <AlertTriangle className="text-red-500" size={24} />
                                    </div>
                                    <div>
                                        <h5 className="text-red-500 font-bold mb-1">Cancel Subscription?</h5>
                                        <p className="text-brand-400 text-sm leading-relaxed">
                                            {socialEnabled
                                                ? 'You will lose access to the Locator, Logbook, and Community features immediately upon cancellation. This action cannot be undone.'
                                                : 'You will lose access to the Locator and Logbook features immediately upon cancellation. This action cannot be undone.'}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex gap-4 justify-end">
                                    <button
                                        onClick={() => setShowCancelConfirm(false)}
                                        className="px-6 py-2.5 text-brand-400 hover:text-brand-100 text-sm font-bold transition-colors"
                                    >
                                        Keep Membership
                                    </button>
                                    <button
                                        onClick={onCancelSubscription}
                                        className="px-6 py-2.5 bg-red-600 hover:bg-red-500 text-white text-sm font-bold rounded-xl shadow-lg shadow-red-500/10 transition-all active:scale-95"
                                    >
                                        Yes, Cancel Subscription
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="flex justify-between items-center">
                                <p className="text-xs text-brand-400">Member since {new Date().getFullYear()}</p>
                                <div className="flex items-center gap-4">
                                    <a
                                        href="https://billing.stripe.com/p/login/00w5kwbwwgzwd8C9V53oA00"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-brand-300 hover:text-gold-500 text-sm font-medium transition-all hover:underline"
                                    >
                                        Manage Subscription
                                    </a>
                                    <button
                                        onClick={() => setShowCancelConfirm(true)}
                                        className="text-red-500 hover:text-red-700 text-sm font-medium transition-all hover:underline flex items-center gap-1"
                                    >
                                        Cancel Subscription
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

            </div>

            {/* Edit Profile Modal */}
            {isEditingProfile && (
                <div className="modal-overlay" onClick={() => setIsEditingProfile(false)}>
                    <div className="modal-content bg-brand-900 border border-brand-800 max-w-md" onClick={e => e.stopPropagation()}>
                        <div className="flex justify-between items-center p-6 border-b border-brand-800">
                            <h3 className="text-xl font-serif text-brand-50">Edit Profile</h3>
                            <button onClick={() => setIsEditingProfile(false)} className="text-brand-400 hover:text-brand-50 transition-colors">
                                <X size={20} />
                            </button>
                        </div>

                        <div className="mb-6 space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-brand-300 uppercase tracking-wider mb-2">Display Name</label>
                                <input
                                    type="text"
                                    value={newName}
                                    onChange={(e) => setNewName(e.target.value)}
                                    className="w-full bg-brand-950 border border-brand-800 rounded-lg p-3 text-brand-50 focus:border-gold-500 outline-none transition-colors"
                                    placeholder="Enter your name..."
                                />
                            </div>

                            {socialEnabled && (
                                <div>
                                    <label className="block text-xs font-bold text-brand-300 uppercase tracking-wider mb-2">
                                        Community Name <span className="text-brand-500 normal-case font-normal">(Club Exclusive)</span>
                                    </label>
                                    <div className="relative">
                                        <span className="absolute left-3 top-3 text-brand-500">@</span>
                                        <input
                                            type="text"
                                            value={newCommunityName}
                                            onChange={(e) => {
                                                if (user.isSubscribed) {
                                                    setNewCommunityName(e.target.value.replace(/[^a-zA-Z0-9_]/g, ''));
                                                    setCommunityNameError('');
                                                }
                                            }}
                                            disabled={!user.isSubscribed}
                                            className={`w-full bg-brand-950 border ${communityNameError ? 'border-red-500' : 'border-brand-800'} rounded-lg p-3 pl-8 text-brand-50 focus:border-gold-500 outline-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
                                            placeholder={user.isSubscribed ? "unique_handle" : "Subscribers only"}
                                        />
                                        {!user.isSubscribed && (
                                            <Lock size={16} className="absolute right-3 top-3.5 text-brand-500" />
                                        )}
                                    </div>
                                    {communityNameError && <p className="text-red-400 text-xs mt-1 flex items-center gap-1"><AlertTriangle size={12} /> {communityNameError}</p>}
                                    <p className="text-brand-500 text-xs mt-1">Unique handle for community features. Active subscription required.</p>
                                </div>
                            )}


                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={() => setIsEditingProfile(false)}
                                className="flex-1 bg-brand-800 hover:bg-brand-700 text-brand-300 font-bold py-3 rounded-lg transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSaveProfile}
                                disabled={!newName.trim() || isCheckingName}
                                className="flex-1 bg-gradient-to-r from-gold-500 to-gold-600 hover:from-gold-400 hover:to-gold-500 text-brand-950 font-bold py-3 rounded-lg shadow-lg flex items-center justify-center gap-2 transition-all disabled:opacity-50"
                            >
                                {isCheckingName ? <Loader2 size={18} className="animate-spin" /> : <Check size={18} />}
                                {isCheckingName ? 'Verifying...' : 'Save Changes'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
